import { Injectable, NgZone, inject } from '@angular/core';
import mixpanel from 'mixpanel-browser';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';

@Injectable({
    providedIn: 'root',
})
export class MixpanelService {
    private router = inject(Router);
    private ngZone = inject(NgZone);

    mixpanelToken: string = environment.mixpanelToken;

    constructor() {
        this.initMixpanel();
    }

    private initMixpanel() {
        if (this.mixpanelToken) {
            // Run outside Angular's zone so the autocapture DOM listeners don't
            // trigger a change-detection storm (which was hanging the app).
            this.ngZone.runOutsideAngular(() => {
                mixpanel.init(this.mixpanelToken, {
                    autocapture: {
                        pageview: 'url-with-path',
                        click: true,
                        input: false,
                        submit: false,
                        capture_text_content: false,
                    },
                });
            });
            console.log('Mixpanel Initialized with Token:', this.mixpanelToken);
        } else {
            console.error('Mixpanel Token is missing in environment.ts');
        }
    }

    /**
     * Initialize Mixpanel for a specific user.
     *
     * @param userEmail - The email of the user.
     */
    init(userEmail: string): void {
        if (!userEmail) {
            console.error(
                'User email is required for Mixpanel initialization.',
            );
            return;
        }

        mixpanel.identify(userEmail);
        console.log(`Mixpanel identified user: ${userEmail}`);
    }

    /**
     * Track a page view event.
     *
     * @param route - The route URL to track.
     */
    trackPageView(route: string): void {
        mixpanel.track('Page View', {
            route: route,
            eventDisplayName: route,
        });
    }

    /**
     * Push a new event to Mixpanel with optional tracking information and route data.
     *
     * @param eventInfo An object containing event-related information (optional).
     * @param route The route URL to track (optional).
     */
    track(
        eventInfo?: {
            id?: string;
            eventName?: string;
            eventDisplayName?: any;
            kpi?: string;
            trigger?: string;
            eventDefinition?: string;
            propertyType?: string;
            propertyDefinition?: string;
            additionalProperties?: object;
            user_id?: string;
        },
        route?: string,
    ): void {
        if (
            !eventInfo ||
            (!eventInfo.eventName && !eventInfo.eventDisplayName)
        ) {
            console.error('Event name is required for tracking.');
            return;
        }

        if (!eventInfo.eventName) {
            eventInfo.eventName = `${eventInfo.eventDisplayName.toLowerCase().replace(/ /g, '_')}`;
        }

        eventInfo.user_id = localStorage.getItem('userEmail') || eventInfo.id;

        // Extract id, eventName, and eventDisplayName from eventInfo, the rest are properties
        const { eventName, ...properties }: any = eventInfo;

        const additionalPropertiesCopy = properties.additionalProperties;

        delete properties.additionalProperties;

        // Include route as a property if provided or use the current URL
        properties['route'] = route || this.router.url;

        // Track the event with Mixpanel
        mixpanel.track(eventName + ' RNP', {
            event: eventName,
            ...properties,
            ...additionalPropertiesCopy,
        });
    }

    /**
     * Register a user in Mixpanel.
     *
     * @param email - The user's email.
     */
    register(email: string) {
        mixpanel.identify(email);
        mixpanel.people.set({
            $name: email,
            $email: email,
        });
    }

    /**
     * Reset Mixpanel user data.
     */
    reset() {
        mixpanel.reset();
    }

    /**
     * Track the start time of a quote process.
     *
     * @param from - The identifier of the starting process.
     */
    trackBeginTime(from: string) {
        mixpanel.track('Quote Start time', {
            [from]: new Date().toLocaleString(),
            eventDisplayName: new Date().toLocaleString(),
        });
    }

    /**
     * Track the end time of a quote process.
     *
     * @param from - The identifier of the ending process.
     */
    trackEndTime(from: string) {
        mixpanel.track('Quote End time', {
            [from]: new Date().toLocaleString(),
            eventDisplayName: new Date().toLocaleString(),
        });
    }
}
